/**
 * Attack Surface Action Options
 *
 * Configuration for the "Change Surface" action menu items.
 * Mirrors severity.tsx — a label row followed by the write-legal surface
 * options. Derives its vocabulary from the shared `attackSurfaceOptions`
 * constant (DRY) filtered to the values the backend accepts on a PUT /risk.
 *
 * Backend WRITE-legal surfaces are internal | external | cloud | repository |
 * application | llm — all six are first-class, writable surfaces.
 */

import { attackSurfaceOptions } from '@/app/constant';

/**
 * The surface values the backend accepts when writing `attackSurface` on a
 * risk. Mirrors the backend `attacksurface.Surface` enum's writable set.
 */
export const RISK_ATTACK_SURFACE_WRITE_VALUES = [
  'internal',
  'external',
  'cloud',
  'repository',
  'application',
  'llm',
] as const;

interface AttackSurfaceOption {
  label: string;
  type?: 'label';
  value?: string;
}

/**
 * Get attack-surface change menu options for a vulnerability.
 * Returns a leading label row plus exactly the 6 write-legal options,
 * derived from the shared `attackSurfaceOptions` vocabulary.
 */
export function getAttackSurfaceChangeOptions(): AttackSurfaceOption[] {
  const writeValues = RISK_ATTACK_SURFACE_WRITE_VALUES as readonly string[];

  return [
    {
      label: 'Change Surface',
      type: 'label',
    },
    ...attackSurfaceOptions
      .filter(
        (
          option
        ): option is (typeof attackSurfaceOptions)[number] & {
          value: string;
        } =>
          typeof option.value === 'string' && writeValues.includes(option.value)
      )
      .map(option => ({
        label: option.label,
        value: option.value,
      })),
  ];
}
